/**
 * Modal Nested Position
 *
 * Positioning helpers for nested modal dialogs and stacked overlays.
 */

import { Roles } from "../constants/roles";
import { CommonUtils } from "./common-utils";
import { FormBase } from "./form-base-form";

const ROLE_BASED_PARENT_MODAL_SELECTOR = [
    `[data-role="${Roles.SEARCH_MENU_CONTAINER}"].visible`,
    `[data-role="${Roles.SEARCH_MENU_POST_PRODUCT_PICKER_CONTAINER}"].visible`,
    `[data-role="${Roles.SEARCH_CATEGORY_MODAL}"].visible`,
    `[data-role="${Roles.SEARCH_BRAND_MODAL}"].visible`,
    `[data-role="${Roles.SEARCH_USER_MODAL}"].visible`,
    `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_MODAL}"].visible`,
    `[data-role="${Roles.ELEMENT_MODAL}"].visible`,
].join(", ");

const ROLE_BASED_MODAL_CONTAINER_SELECTOR = [
    `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_MODAL}"]`,
    `[data-role="${Roles.SEARCH_MENU_CONTAINER}"]`,
    `[data-role="${Roles.SEARCH_MENU_POST_PRODUCT_PICKER_CONTAINER}"]`,
    `[data-role="${Roles.SEARCH_CATEGORY_MODAL}"]`,
    `[data-role="${Roles.SEARCH_BRAND_MODAL}"]`,
    `[data-role="${Roles.SEARCH_USER_MODAL}"]`,
    `[data-role="${Roles.ELEMENT_MODAL}"]`,
].join(", ");

const DEFAULT_PARENT_MODAL_SELECTOR = `${ROLE_BASED_PARENT_MODAL_SELECTOR}, .navbar-notifications-popover.visible, .avatar-modal-dialog.visible, .modal-dialog.visible, .modal.visible`;
const DEFAULT_TITLEBAR_SELECTOR = `[data-role="${Roles.ELEMENT_MODAL_HEADER}"]`;

class ModalNestedPositionFormBase extends FormBase {
    public getElements(root: ParentNode, selector: string): HTMLElement[] {
        return this.queryElements(root, selector, HTMLElement);
    }

    public getClosest(root: Event | EventTarget | Element | null | undefined, selector: string): HTMLElement | null {
        return this.closest(root, selector, HTMLElement);
    }

    public getElement(root: ParentNode | null | undefined, selector: string): HTMLElement | null {
        return this.queryElement(root, selector, HTMLElement);
    }
}

const modalNestedPositionFormBase = new ModalNestedPositionFormBase();
const DEFAULT_MODAL_CONTAINER_SELECTOR = `${ROLE_BASED_MODAL_CONTAINER_SELECTOR}, .navbar-notifications-popover, .avatar-modal-dialog, .modal-dialog, .modal`;

const DEFAULT_MARGIN = 8;
const DEFAULT_OFFSET_X = 36;
const DEFAULT_OFFSET_Y = 42;
const DEFAULT_FALLBACK_LEFT = 12;
const DEFAULT_FALLBACK_TOP = 12;
const DEFAULT_NESTED_CLASS_NAME = "is-nested-modal";
const DEFAULT_VISIBLE_CLASS_NAME = "visible";
const DEFAULT_DISPLAY_VALUE = "flex";
const DEFAULT_OVERLAY_DISPLAY_VALUE = "block";
const RESET_STYLE_PROPERTIES = [
    "left",
    "top",
    "right",
    "bottom",
    "width",
    "height",
    "max-height",
    "min-height",
    "margin",
    "position",
    "transform",
    "will-change",
] as const;
const DEFAULT_DRAGGING_CLASS_NAME = "is-dragging";
const DEFAULT_DRAG_INITIALIZED_DATA_KEY = "dragInitialized";

interface FindParentOptions {
    parentModalSelector?: string;
    titlebarSelector?: string;
    modalContainerSelector?: string;
}

interface ApplyNestedOpenPositionOptions {
    parentEl?: HTMLElement | null;
    overlayEl?: HTMLElement | null;
    offsetX?: number;
    offsetY?: number;
    margin?: number;
    fallbackLeft?: number;
    fallbackTop?: number;
    nestedClassName?: string;
}

interface OpenModalParams {
    modalEl: HTMLElement;
    displayValue?: string;
    visibleClassName?: string;
    deferVisibleClass?: boolean;
}

interface CloseModalParams {
    modalEl: HTMLElement;
    visibleClassName?: string;
    hideDelayMs?: number;
}

interface OpenOverlayModalParams {
    overlayEl: HTMLElement;
    modalEl: HTMLElement;
    overlayDisplayValue?: string;
    modalDisplayValue?: string;
    visibleClassName?: string;
    deferVisibleClass?: boolean;
}

interface CloseOverlayModalParams {
    overlayEl: HTMLElement;
    modalEl: HTMLElement;
    visibleClassName?: string;
    hideDelayMs?: number;
}

interface ResetBeforeOpenOptions {
    draggingClassName?: string;
    dragInitializedDataKey?: string;
    removeStyleProperties?: string[];
}

interface OpenAsNestedParams {
    modalRootEl: HTMLElement;
    windowEl?: HTMLElement | null;
    windowSelector?: string;
    parentEl?: HTMLElement | null;
    parentModalSelector?: string;
    overlayEl?: HTMLElement | null;
    visibleClassName?: string;
    displayValue?: string;
    setWindowFixed?: boolean;
    resetWindowBeforeOpen?: boolean;
    nestedClassName?: string;
    offsetX?: number;
    offsetY?: number;
    margin?: number;
    fallbackLeft?: number;
    fallbackTop?: number;
}

interface OpenAsNestedResult {
    parentEl: HTMLElement | null;
    windowEl: HTMLElement | null;
}

interface OpenOverlayModalAsNestedParams {
    overlayEl: HTMLElement;
    modalEl: HTMLElement;
    parentModalSelector?: string;
    raiseZIndexAboveParent?: boolean;
    overlayZIndexOffset?: number;
    modalZIndexOffset?: number;
    overlayDisplayValue?: string;
    modalDisplayValue?: string;
    visibleClassName?: string;
    deferVisibleClass?: boolean;
    resetModalBeforeOpen?: boolean;
    nestedClassName?: string;
    offsetX?: number;
    offsetY?: number;
    margin?: number;
    fallbackLeft?: number;
    fallbackTop?: number;
}

interface OpenOverlayModalAsNestedResult {
    parentEl: HTMLElement | null;
}

function isHTMLElement(el: Element | null | undefined): el is HTMLElement {
    return el instanceof HTMLElement;
}

function isVisible(el: HTMLElement): boolean {
    try {
        const view = el.ownerDocument?.defaultView;
        const cs = view?.getComputedStyle ? view.getComputedStyle(el) : null;
        if (!cs) return Boolean(el.getClientRects().length);
        if (cs.display === "none" || cs.visibility === "hidden" || cs.opacity === "0") return false;
    } catch {
        // ignore
    }
    return Boolean(el.getClientRects().length);
}

function queryElements(doc: Document, selector: string): HTMLElement[] {
    return modalNestedPositionFormBase.getElements(doc, selector);
}

export function findLastOpenParentModalBySelectors(
    doc: Document,
    excludeEl: HTMLElement | null | undefined,
    parentModalSelector: string,
    titlebarSelector = DEFAULT_TITLEBAR_SELECTOR,
    modalContainerSelector = DEFAULT_MODAL_CONTAINER_SELECTOR,
): HTMLElement | null {
    if (!(doc instanceof Document)) return null;

    const directCandidates = queryElements(doc, parentModalSelector)
        .filter((el) => el !== excludeEl)
        .filter((el) => isVisible(el));
    if (directCandidates.length > 0) {
        // Prefer the candidate with the highest data-modal-depth (most deeply nested visible
        // modal). DOM order is unreliable because dynamically-appended overlays (e.g. the
        // search menu) end up last in the DOM regardless of actual nesting order.
        const withDepth = directCandidates.filter((el) => el.dataset.modalDepth !== undefined);
        if (withDepth.length > 0) {
            return withDepth.reduce((best, el) => {
                const bestD = Math.max(0, Number(best.dataset.modalDepth) || 0);
                const elD = Math.max(0, Number(el.dataset.modalDepth) || 0);
                return elD > bestD ? el : best;
            });
        }
        return directCandidates[directCandidates.length - 1] || null;
    }

    const titlebars = queryElements(doc, titlebarSelector).filter((el) => isVisible(el));
    const containers: HTMLElement[] = [];
    titlebars.forEach((titlebar) => {
        const container = modalNestedPositionFormBase.getClosest(titlebar, modalContainerSelector);
        if (!isHTMLElement(container) || container === excludeEl) return;
        if (containers.includes(container)) return;
        containers.push(container);
    });

    if (containers.length === 0) return null;
    return containers[containers.length - 1] || null;
}

export function applyNestedOpenPosition(modalEl: HTMLElement, options: ApplyNestedOpenPositionOptions = {}): void {
    if (!(modalEl instanceof HTMLElement)) return;

    const parentEl = options.parentEl instanceof HTMLElement ? options.parentEl : null;
    const overlayEl = options.overlayEl instanceof HTMLElement ? options.overlayEl : null;
    const margin = Number.isFinite(options.margin) ? Number(options.margin) : DEFAULT_MARGIN;
    const isMobile = CommonUtils.isMobileViewport();
    const defaultOffsetX = isMobile ? 0 : DEFAULT_OFFSET_X;
    const offsetX = Number.isFinite(options.offsetX) ? Number(options.offsetX) : defaultOffsetX;
    const offsetY = Number.isFinite(options.offsetY) ? Number(options.offsetY) : DEFAULT_OFFSET_Y;
    const fallbackLeft = Number.isFinite(options.fallbackLeft) ? Number(options.fallbackLeft) : DEFAULT_FALLBACK_LEFT;
    const fallbackTop = Number.isFinite(options.fallbackTop) ? Number(options.fallbackTop) : DEFAULT_FALLBACK_TOP;
    const nestedClassName = String(options.nestedClassName || DEFAULT_NESTED_CLASS_NAME);

    const viewportEl = overlayEl?.ownerDocument?.documentElement || document.documentElement;
    const viewportW = viewportEl?.clientWidth || 0;
    const viewportH = viewportEl?.clientHeight || 0;
    const selfRect = modalEl.getBoundingClientRect();

    if (!(parentEl instanceof HTMLElement)) {
        delete modalEl.dataset.modalDepth;
        modalEl.classList.remove(nestedClassName);
        modalEl.style.left = `${Math.round(Math.max(margin, fallbackLeft))}px`;
        modalEl.style.top = `${Math.round(Math.max(margin, fallbackTop))}px`;
        modalEl.style.removeProperty("right");
        modalEl.style.removeProperty("bottom");
        return;
    }

    // On mobile: depth = parent's depth + 1, cascading through the modal stack.
    // data-modal-depth is read by SCSS @for rule to set top/height/width/left.
    if (isMobile) {
        const parentDepth = Number.isFinite(Number(parentEl.dataset.modalDepth))
            ? Math.max(0, Number(parentEl.dataset.modalDepth))
            : 0;
        const depth = parentDepth + 1;
        modalEl.dataset.modalDepth = String(depth);
        modalEl.classList.add(nestedClassName);
        modalEl.style.removeProperty("right");
        modalEl.style.removeProperty("bottom");
        return;
    }

    // Desktop: arithmetic progression from fixed base (Windows Explorer diagonal staircase).
    // Not all modals have the CSS transform that uses --nested-offset-x/y, so we zero out
    // those variables via inline style and let JS own the full position calculation.
    const parentDepth = Number.isFinite(Number(parentEl.dataset.modalDepth))
        ? Math.max(0, Number(parentEl.dataset.modalDepth))
        : 0;
    const depth = parentDepth + 1;
    modalEl.dataset.modalDepth = String(depth);

    const maxLeft = Math.max(margin, viewportW - margin - selfRect.width);
    const maxTop = Math.max(margin, viewportH - margin - selfRect.height);
    const left = Math.max(margin, Math.min(fallbackLeft + depth * offsetX, maxLeft));
    const top = Math.max(margin, Math.min(fallbackTop + depth * offsetY, maxTop));

    modalEl.classList.add(nestedClassName);
    // Zero out CSS transform variables so translate(--nested-offset-x, --nested-offset-y)
    // on modals that have it does not double-offset the position.
    modalEl.style.setProperty("--nested-offset-x", "0px");
    modalEl.style.setProperty("--nested-offset-y", "0px");
    modalEl.style.left = `${Math.round(left)}px`;
    modalEl.style.top = `${Math.round(top)}px`;
    modalEl.style.removeProperty("right");
    modalEl.style.removeProperty("bottom");
}

export function openModal(params: OpenModalParams): void {
    const modalEl = params?.modalEl;
    if (!(modalEl instanceof HTMLElement)) return;

    ModalNestedPosition.resetBeforeOpen(modalEl);

    const displayValue = String(params.displayValue || DEFAULT_DISPLAY_VALUE);
    const visibleClassName = String(params.visibleClassName || DEFAULT_VISIBLE_CLASS_NAME);
    const deferVisibleClass = params.deferVisibleClass === true;

    modalEl.hidden = false;
    modalEl.style.display = displayValue;

    const applyVisible = (): void => {
        modalEl.classList.add(visibleClassName);
    };

    if (deferVisibleClass) {
        requestAnimationFrame(() => applyVisible());
    } else {
        applyVisible();
    }
}

export function closeModal(params: CloseModalParams): void {
    const modalEl = params?.modalEl;
    if (!(modalEl instanceof HTMLElement)) return;

    const visibleClassName = String(params.visibleClassName || DEFAULT_VISIBLE_CLASS_NAME);
    const hideDelayMs = Number.isFinite(params?.hideDelayMs) ? Math.max(0, Number(params.hideDelayMs)) : 0;

    modalEl.classList.remove(visibleClassName);

    const hide = (): void => {
        if (modalEl.classList.contains(visibleClassName)) return;
        modalEl.hidden = true;
        modalEl.style.display = "none";
    };

    if (hideDelayMs > 0) {
        setTimeout(() => hide(), hideDelayMs);
    } else {
        hide();
    }
}

export function openOverlayModal(params: OpenOverlayModalParams): void {
    const overlayEl = params?.overlayEl;
    const modalEl = params?.modalEl;
    if (!(overlayEl instanceof HTMLElement) || !(modalEl instanceof HTMLElement)) return;

    ModalNestedPosition.resetBeforeOpen(modalEl);

    const visibleClassName = String(params.visibleClassName || DEFAULT_VISIBLE_CLASS_NAME);
    const overlayDisplayValue = String(params.overlayDisplayValue || DEFAULT_OVERLAY_DISPLAY_VALUE);
    const modalDisplayValue = String(params.modalDisplayValue || DEFAULT_DISPLAY_VALUE);
    const deferVisibleClass = params.deferVisibleClass === true;

    overlayEl.hidden = false;
    overlayEl.style.display = overlayDisplayValue;
    modalEl.hidden = false;
    modalEl.style.display = modalDisplayValue;

    const applyVisible = (): void => {
        overlayEl.classList.add(visibleClassName);
        modalEl.classList.add(visibleClassName);
    };

    if (deferVisibleClass) {
        requestAnimationFrame(() => applyVisible());
    } else {
        applyVisible();
    }
}

export function closeOverlayModal(params: CloseOverlayModalParams): void {
    const overlayEl = params?.overlayEl;
    const modalEl = params?.modalEl;
    if (!(overlayEl instanceof HTMLElement) || !(modalEl instanceof HTMLElement)) return;

    const visibleClassName = String(params.visibleClassName || DEFAULT_VISIBLE_CLASS_NAME);
    const hideDelayMs = Number.isFinite(params?.hideDelayMs) ? Math.max(0, Number(params.hideDelayMs)) : 0;

    overlayEl.classList.remove(visibleClassName);
    modalEl.classList.remove(visibleClassName);

    const hide = (): void => {
        if (!overlayEl.classList.contains(visibleClassName)) {
            overlayEl.hidden = true;
            overlayEl.style.display = "none";
        }
        if (!modalEl.classList.contains(visibleClassName)) {
            modalEl.hidden = true;
            modalEl.style.display = "none";
        }
    };

    if (hideDelayMs > 0) {
        setTimeout(() => hide(), hideDelayMs);
    } else {
        hide();
    }
}

export class ModalNestedPosition {
    public static DEFAULT_PARENT_MODAL_SELECTOR = DEFAULT_PARENT_MODAL_SELECTOR;

    public static DEFAULT_TITLEBAR_SELECTOR = DEFAULT_TITLEBAR_SELECTOR;

    public static DEFAULT_MODAL_CONTAINER_SELECTOR = DEFAULT_MODAL_CONTAINER_SELECTOR;

    public static DEFAULT_MARGIN = DEFAULT_MARGIN;

    public static DEFAULT_OFFSET_X = DEFAULT_OFFSET_X;

    public static DEFAULT_OFFSET_Y = DEFAULT_OFFSET_Y;

    public static DEFAULT_FALLBACK_LEFT = DEFAULT_FALLBACK_LEFT;

    public static DEFAULT_FALLBACK_TOP = DEFAULT_FALLBACK_TOP;

    public static DEFAULT_NESTED_CLASS_NAME = DEFAULT_NESTED_CLASS_NAME;

    public static DEFAULT_VISIBLE_CLASS_NAME = DEFAULT_VISIBLE_CLASS_NAME;

    public static DEFAULT_DISPLAY_VALUE = DEFAULT_DISPLAY_VALUE;

    public static DEFAULT_OVERLAY_DISPLAY_VALUE = DEFAULT_OVERLAY_DISPLAY_VALUE;

    public static DEFAULT_DRAGGING_CLASS_NAME = DEFAULT_DRAGGING_CLASS_NAME;

    public static DEFAULT_DRAG_INITIALIZED_DATA_KEY = DEFAULT_DRAG_INITIALIZED_DATA_KEY;

    public static findLastOpenParentModal(
        doc: Document,
        excludeEl: HTMLElement | null | undefined,
        options: FindParentOptions = {},
    ): HTMLElement | null {
        const parentModalSelector = String(options.parentModalSelector || DEFAULT_PARENT_MODAL_SELECTOR);
        const titlebarSelector = String(options.titlebarSelector || DEFAULT_TITLEBAR_SELECTOR);
        const modalContainerSelector = String(options.modalContainerSelector || DEFAULT_MODAL_CONTAINER_SELECTOR);
        return findLastOpenParentModalBySelectors(
            doc,
            excludeEl,
            parentModalSelector,
            titlebarSelector,
            modalContainerSelector,
        );
    }

    public static applyOpenPosition(modalEl: HTMLElement, options: ApplyNestedOpenPositionOptions = {}): void {
        return applyNestedOpenPosition(modalEl, {
            parentEl: options.parentEl,
            overlayEl: options.overlayEl,
            offsetX: options.offsetX,
            offsetY: options.offsetY,
            margin: Number.isFinite(options.margin) ? Number(options.margin) : DEFAULT_MARGIN,
            fallbackLeft: Number.isFinite(options.fallbackLeft) ? Number(options.fallbackLeft) : DEFAULT_FALLBACK_LEFT,
            fallbackTop: Number.isFinite(options.fallbackTop) ? Number(options.fallbackTop) : DEFAULT_FALLBACK_TOP,
            nestedClassName: String(options.nestedClassName || DEFAULT_NESTED_CLASS_NAME),
        });
    }

    public static resetBeforeOpen(modalEl: HTMLElement | null | undefined, options: ResetBeforeOpenOptions = {}): void {
        if (!(modalEl instanceof HTMLElement)) return;
        const draggingClassName = String(options.draggingClassName || DEFAULT_DRAGGING_CLASS_NAME);
        const dragInitializedDataKey = String(options.dragInitializedDataKey || DEFAULT_DRAG_INITIALIZED_DATA_KEY);
        const removeStyleProperties = Array.isArray(options.removeStyleProperties)
            ? options.removeStyleProperties
            : [...RESET_STYLE_PROPERTIES];

        modalEl.classList.remove(draggingClassName);
        removeStyleProperties.forEach((prop) => {
            const key = String(prop || "").trim();
            if (!key) return;
            modalEl.style.removeProperty(key);
        });

        if (dragInitializedDataKey) {
            delete modalEl.dataset[dragInitializedDataKey];
        }
    }

    public static openModal(params: OpenModalParams): void {
        return openModal(params);
    }

    public static closeModal(params: CloseModalParams): void {
        return closeModal(params);
    }

    public static openOverlayModal(params: OpenOverlayModalParams): void {
        return openOverlayModal(params);
    }

    public static closeOverlayModal(params: CloseOverlayModalParams): void {
        return closeOverlayModal(params);
    }

    public static openAsNested(params: OpenAsNestedParams): OpenAsNestedResult {
        const modalRootEl = params?.modalRootEl;
        if (!(modalRootEl instanceof HTMLElement)) {
            return { parentEl: null, windowEl: null };
        }

        const visibleClassName = String(params.visibleClassName || DEFAULT_VISIBLE_CLASS_NAME);
        const displayValue = String(params.displayValue || DEFAULT_DISPLAY_VALUE);
        const setWindowFixed = params.setWindowFixed !== false;
        const resetWindowBeforeOpen = params.resetWindowBeforeOpen !== false;

        const windowElByParam = params.windowEl instanceof HTMLElement ? params.windowEl : null;
        const windowSelector = String(params.windowSelector || "").trim();
        const windowElBySelector = windowSelector
            ? (() => {
                  const found = modalNestedPositionFormBase.getElement(modalRootEl, windowSelector);
                  return found instanceof HTMLElement ? found : null;
              })()
            : null;
        const targetWindowEl = windowElByParam || windowElBySelector;

        if (resetWindowBeforeOpen) {
            ModalNestedPosition.resetBeforeOpen(targetWindowEl);
        }

        modalRootEl.hidden = false;
        modalRootEl.style.display = displayValue;
        modalRootEl.classList.add(visibleClassName);

        if (!(targetWindowEl instanceof HTMLElement)) {
            return { parentEl: null, windowEl: null };
        }

        if (setWindowFixed) {
            targetWindowEl.style.position = "fixed";
        }

        const parentEl =
            params.parentEl instanceof HTMLElement
                ? params.parentEl
                : ModalNestedPosition.findLastOpenParentModal(document, targetWindowEl, {
                      parentModalSelector: params.parentModalSelector,
                  });

        ModalNestedPosition.applyOpenPosition(targetWindowEl, {
            parentEl,
            overlayEl: params.overlayEl,
            nestedClassName: params.nestedClassName,
            offsetX: params.offsetX,
            offsetY: params.offsetY,
            margin: params.margin,
            fallbackLeft: params.fallbackLeft,
            fallbackTop: params.fallbackTop,
        });

        return {
            parentEl: parentEl instanceof HTMLElement ? parentEl : null,
            windowEl: targetWindowEl,
        };
    }

    public static openOverlayModalAsNested(params: OpenOverlayModalAsNestedParams): OpenOverlayModalAsNestedResult {
        const overlayEl = params?.overlayEl;
        const modalEl = params?.modalEl;
        if (!(overlayEl instanceof HTMLElement) || !(modalEl instanceof HTMLElement)) {
            return { parentEl: null };
        }

        const visibleClassName = String(params.visibleClassName || DEFAULT_VISIBLE_CLASS_NAME);
        const overlayDisplayValue = String(params.overlayDisplayValue || DEFAULT_OVERLAY_DISPLAY_VALUE);
        const modalDisplayValue = String(params.modalDisplayValue || DEFAULT_DISPLAY_VALUE);
        const deferVisibleClass = params.deferVisibleClass === true;
        const resetModalBeforeOpen = params.resetModalBeforeOpen !== false;
        const raiseZIndexAboveParent = params.raiseZIndexAboveParent === true;
        const overlayZIndexOffset = Number.isFinite(params.overlayZIndexOffset)
            ? Number(params.overlayZIndexOffset)
            : 1;
        const modalZIndexOffset = Number.isFinite(params.modalZIndexOffset) ? Number(params.modalZIndexOffset) : 2;

        if (resetModalBeforeOpen) {
            ModalNestedPosition.resetBeforeOpen(modalEl);
        }

        const parentEl = ModalNestedPosition.findLastOpenParentModal(document, modalEl, {
            parentModalSelector: params.parentModalSelector,
        });

        if (raiseZIndexAboveParent && parentEl instanceof HTMLElement) {
            // parentEl may be the inner `.element-modal` (found via the
            // [data-role=ELEMENT_MODAL].visible selector) rather than its
            // `.element-modal-overlay` ancestor. The inner modal's own z-index
            // (e.g. $z-element-modal=611) only matters WITHIN that overlay's own
            // stacking context — it's the overlay's z-index that actually
            // competes against this nested picker's own overlay for top-level
            // stacking. Some overlays raise their z-index above the generic
            // tier (e.g. .element-modal-overlay--review-create at +20), so
            // reading from the inner modal here would under-raise the picker
            // and leave it rendered behind that overlay. Climb to the overlay
            // ancestor when present so the real competing z-index is used.
            const zIndexSourceEl = modalNestedPositionFormBase.getClosest(parentEl, ".element-modal-overlay") ?? parentEl;
            const view = zIndexSourceEl.ownerDocument?.defaultView;
            const parentZRaw = view?.getComputedStyle?.(zIndexSourceEl)?.zIndex || "";
            const parentZ = Number.parseInt(String(parentZRaw), 10);
            if (Number.isFinite(parentZ)) {
                overlayEl.style.zIndex = String(parentZ + overlayZIndexOffset);
                modalEl.style.zIndex = String(parentZ + modalZIndexOffset);
            }
        }

        overlayEl.hidden = false;
        overlayEl.style.display = overlayDisplayValue;
        modalEl.hidden = false;
        modalEl.style.display = modalDisplayValue;

        ModalNestedPosition.applyOpenPosition(modalEl, {
            parentEl,
            overlayEl,
            nestedClassName: params.nestedClassName,
            offsetX: params.offsetX,
            offsetY: params.offsetY,
            margin: params.margin,
            fallbackLeft: params.fallbackLeft,
            fallbackTop: params.fallbackTop,
        });

        const applyVisible = (): void => {
            overlayEl.classList.add(visibleClassName);
            modalEl.classList.add(visibleClassName);
        };

        if (deferVisibleClass) {
            requestAnimationFrame(() => applyVisible());
        } else {
            applyVisible();
        }

        return {
            parentEl: parentEl instanceof HTMLElement ? parentEl : null,
        };
    }
}
